import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { BsArrowsCollapse } from 'react-icons/bs';
import { ExperienceVariant, YesNo } from '@/webapi/use-experience-api';
import { Card } from '@/components/card';
import { DeviceType } from '@/utils/definitions';
import { EditorContext } from '@/features/editor/context/editor-context';
import { VSpace } from '@/components/spacing';
import {
  DefaultInlineVariantsLayoutHeader,
  InlineVariantsLayoutHeaderText,
} from '@/features/editor/widgets/shared/inline-variants-layout/default-header';
import { InlineVariantsLayoutControlGroup } from '@/features/editor/widgets/shared/inline-variants-layout/control';
import { InlineVariantsLayoutVariation } from '@/features/editor/widgets/shared/inline-variants-layout/variant';
import { InlineVariantsLayoutAddVariantButton } from '@/features/editor/widgets/shared/inline-variants-layout/add-variant-button';
import { InlineVariantsLayoutGoals } from '@/features/editor/widgets/shared/inline-variants-layout/goals';
import { CircularCheckmark } from '@/components/circular-checkmark';
import { Alert } from '@/components/alert';
import { ProductSelectorForAnalytics } from '@/features/editor/widgets/shared/inline-variants-layout/product-analytics-selector';
// @ts-ignore
import ga4Icon from '../../../../../assets/book_icon.svg';
import { FeatureBit } from '@/webapi/use-auth-api';
import { useFeatureBit } from '@/features/account-context';
import { isPriceTestExperience } from '@/utils/experience-utils';
import { ShopifyOrderTagsModal } from '@/features/editor/widgets/shared/inline-variants-layout/shopify-order-tags-modal';
import { useBaselineVariant } from '@/features/editor/widgets/shared/inline-variants-layout/use-baseline-variant';
import { useShopifyOrderTags } from '@/features/editor/widgets/shared/inline-variants-layout/use-shopify-order-tags';

export interface InlineVariantsLayoutProps {
  className?: string;

  hideGoals?: boolean;
  hideGA?: boolean;

  header?: React.ReactNode;
  footer?: React.ReactNode;

  controlDescription?: React.ReactNode;
  controlAction?: React.ReactNode;
  variantAction?: (
    variant: ExperienceVariant,
    variantIdx: number,
  ) => React.ReactNode;
  variantDescription?: (
    variant: ExperienceVariant,
    variantIdx: number,
  ) => React.ReactNode;
  variantContent?: (
    variant: ExperienceVariant,
    variantIdx: number,
  ) => React.ReactNode;

  disableActiveStyles?: boolean;
}

export function InlineVariantsLayout({
  className,
  header,
  footer,
  hideGoals,
  hideGA,
  controlDescription,
  controlAction,
  variantDescription,
  variantAction,
  variantContent,
  disableActiveStyles,
}: InlineVariantsLayoutProps) {
  const {
    experienceState,
    devicePreview,
    resources: { gaIntegrationEnabled },
  } = useContext(EditorContext);
  const {
    currentExperience,
    toggleGaEvent,
    toggleShopifyOrderTagging,
    setShopifyOrderTags,
    setEditorBaselineVariantId,
  } = experienceState;

  const {
    editorState: { device },
  } = devicePreview;
  const { variants } = currentExperience;

  const {
    isBaselineEditMode,
    baselineVariantId,
    showUpdateBaseline,
    handleSelectBaseline,
    onToggleBaseline,
  } = useBaselineVariant(
    currentExperience,
    variants,
    setEditorBaselineVariantId,
  );

  const {
    showModal: showShopifyOrderTagsModal,
    tagsDraft: shopifyOrderTagsDraft,
    triggerRef: shopifyOrderTaggingTriggerRef,
    openModal: openShopifyOrderTagsModal,
    onLabelChange: onShopifyOrderTagLabelChange,
    onSave: saveShopifyOrderTags,
    onClose: closeShopifyOrderTagsModal,
  } = useShopifyOrderTags(
    currentExperience,
    variants,
    toggleShopifyOrderTagging,
    setShopifyOrderTags,
  );

  const isPriceTest = isPriceTestExperience(currentExperience);
  const isPostPurchase = currentExperience.isPostPurchase === true;
  const gaEventsEnabled =
    currentExperience.targeting.sendGAEvents === YesNo.YES;
  const shopifyOrderTaggingEnabled = !!currentExperience.tagShopifyOrders;

  const [analyzeProducts, setAnalyzeProducts] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);

  const perProductAnalyticsEnabled = useFeatureBit(
    FeatureBit.ANALYZE_EXPERIENCE_SEPERATE_PRODUCTS,
  );

  const shopifyOrderTaggingFeatureEnabled = useFeatureBit(
    FeatureBit.TAG_SHOPIFY_ORDERS,
  );

  const showGaField = gaIntegrationEnabled && !hideGA;
  const showPerProductAnalyticsField =
    perProductAnalyticsEnabled && !isPriceTest && !isPostPurchase;

  const headerRightContent = showUpdateBaseline ? (
    <UpdateBaselineWrapper
      className="update-baseline"
      enabled={isBaselineEditMode}
    >
      <UpdateText onClick={onToggleBaseline}>Update Baseline</UpdateText>
      <BsArrowsCollapse size={20} />
    </UpdateBaselineWrapper>
  ) : undefined;

  return (
    <Wrapper className={className} device={device}>
      {header || (
        <DefaultInlineVariantsLayoutHeader rightContent={headerRightContent} />
      )}
      <VSpace value={2} />
      <InlineVariantsLayoutControlGroup
        device={device}
        description={controlDescription}
        action={controlAction}
      />
      {variants?.map((variant) => (
        <div key={variant.id}>
          <VSpace value={2} />
          <InlineVariantsLayoutVariation
            device={device}
            variant={variant}
            variantDescription={variantDescription?.(
              variant,
              variants.indexOf(variant),
            )}
            variantContent={variantContent?.(
              variant,
              variants.indexOf(variant),
            )}
            variantAction={variantAction?.(variant, variants.indexOf(variant))}
            disableActiveStyles={disableActiveStyles}
            updateEnabled={isBaselineEditMode && showUpdateBaseline}
            baselineVariantId={baselineVariantId}
            setBaselineVariantId={handleSelectBaseline}
          />
        </div>
      ))}
      <InlineVariantsLayoutAddVariantButton />
      {!hideGoals && (
        <>
          <VSpace value={2} />
          <InlineVariantsLayoutGoals />
        </>
      )}
      {(showPerProductAnalyticsField || showGaField) && (
        <>
          <VSpace value={3} />
          <InlineVariantsLayoutHeaderText device={device}>
            Advanced testing settings
          </InlineVariantsLayoutHeaderText>
        </>
      )}
      {showPerProductAnalyticsField && (
        <>
          <VSpace value={2.5} />
          <CircularCheckmark
            square
            selected={analyzeProducts}
            onChange={(value) => {
              setAnalyzeProducts(value);
              if (value) setShowProductModal(true);
            }}
            caption={
              <span>
                Analyze experience impact on{` `}
                <ProductLink
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setShowProductModal(true);
                  }}
                >
                  selected products
                </ProductLink>
                {` `}separately
              </span>
            }
          />
          <ProductSelectorForAnalytics
            isVisible={showProductModal}
            onClose={() => setShowProductModal(false)}
          />
        </>
      )}
      {showGaField && (
        <>
          <VSpace value={2.5} />
          <CircularCheckmark
            square
            selected={gaEventsEnabled}
            caption="Send events to google analytics"
            onChange={toggleGaEvent}
          />
        </>
      )}
      {gaEventsEnabled && (
        <>
          <VSpace value={1.5} />
          <Alert
            type="info"
            text="How to create a segment in GA4 based on different variations >"
            icon={ga4Icon}
            href="https://help.visually.io/visually.io-google-analytics-4-integration-visually-help-center"
          />
        </>
      )}
      {shopifyOrderTaggingFeatureEnabled && (
        <>
          <VSpace value={2.5} />
          <ShopifyOrderTaggingTrigger ref={shopifyOrderTaggingTriggerRef}>
            <CircularCheckmark
              square
              selected={shopifyOrderTaggingEnabled}
              caption="Apply tags to Shopify orders"
              onChange={(selected) => {
                if (selected) {
                  openShopifyOrderTagsModal();
                  return;
                }
                toggleShopifyOrderTagging(false);
              }}
            />
          </ShopifyOrderTaggingTrigger>
        </>
      )}

      {shopifyOrderTaggingFeatureEnabled && shopifyOrderTaggingEnabled && (
        <>
          <VSpace value={1.5} />
          <Alert
            type="info"
            text="Tags are applied once attribution data is available"
            icon={ga4Icon}
          />
        </>
      )}

      <ShopifyOrderTagsModal
        isVisible={showShopifyOrderTagsModal}
        fromRef={shopifyOrderTaggingTriggerRef}
        experienceName={currentExperience.name}
        variants={variants}
        tags={shopifyOrderTagsDraft}
        onClose={closeShopifyOrderTagsModal}
        onSave={saveShopifyOrderTags}
        onChange={onShopifyOrderTagLabelChange}
      />

      {footer}
    </Wrapper>
  );
}

const Wrapper = styled(Card)`
  && {
    width: ${({ device }: { device: DeviceType }) =>
      device === DeviceType.Desktop ? `100%` : `100%`};
    padding: ${({ device }: { device: DeviceType }) =>
      device === DeviceType.Desktop ? `2rem` : `3rem`};

    cursor: default;

    :hover {
      background: white;
    }

    :active {
      background: white;
    }
  }
`;

const ProductLink = styled.span`
  cursor: pointer;
  text-decoration: underline;
  font-weight: 500;

  &:hover {
    opacity: 0.8;
  }
`;
const ShopifyOrderTaggingTrigger = styled.div``;

const UpdateBaselineWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${(p: { enabled: boolean }) => (p.enabled ? `#0026EF` : `#A5AEB6`)};
  gap: 0.5rem;
  line-height: 18px;
  font-weight: 500;
  flex-shrink: 0;
  justify-content: end;
`;

const UpdateText = styled.p`
  font-family: Inter, serif;
  font-size: 13px;
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;
  margin: 0;
  user-select: none;
`;
